/***
 *
 *   MCP  (Location MCP)
 *   Dedicated tab for the MapAtlas MCP product: pooled monthly request balance,
 *   connect-by-link setup (per-client), usage breakdown, a "which plan fits you?"
 *   calculator, and a pay-as-you-go rate card.
 *
 *   Pricing model:
 *     - Free tier: 15,000 requests/mo, no card.
 *     - Tier ladder priced in requests/mo (weighted: a place/nearby search = ~5
 *       requests, advanced routing = ~2, everything else = 1).
 *     - Pay-as-you-go / overage billed per call.
 *
 *   v1 is frontend-only with placeholder usage data. Real usage will come from the
 *   CF gateway and limits/rates from a shared config.
 *
 **********/

import { Fragment, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, Link } from "react-router-dom";

const VIOLET = "#a78bfa";
const fmt = (n) => n.toLocaleString();
const short = (n) => (n >= 1000000 ? `${(n / 1000000).toFixed(n % 1000000 ? 1 : 0)}M` : `${Math.round(n / 1000)}k`);

// ---- tier ladder (requests/mo) ----
const TIERS = [
  { name: "Free", price: "€0", requests: 12000, blurb: "Build + test", paid: false },
  { name: "Starter", price: "€12", requests: 30000, blurb: "First real use", paid: true },
  { name: "Plus", price: "€49", requests: 120000, blurb: "Indie / small app", paid: true, recommended: true },
  { name: "Pro", price: "€169", requests: 500000, blurb: "High volume", paid: true },
  { name: "Scale", price: "€399", requests: 1500000, blurb: "Serious scale", paid: true },
];
const ENTERPRISE = { name: "Enterprise", price: "Talk to us", reqps: "Custom", support: "Dedicated + SLA", detail: "Custom volume", blurb: "Platforms" };
const FREE = TIERS[0];

// request weights: heavy calls count as more than one request
const WEIGHTS = { geocode: 1, autocomplete: 1, search: 5, directions: 1, matrix: 1, advanced: 2 };

// pay-as-you-go / overage rates per 1,000 requests
const PAYG_RATES = [
  { type: "Geocoding / reverse", ours: 2.5 },
  { type: "Autocomplete / lookup", ours: 1.5 },
  { type: "Nearby / place / text search", ours: 15 },
  { type: "Directions / route", ours: 2.5 },
  { type: "Distance matrix (per element)", ours: 2.5 },
  { type: "Advanced / traffic-aware routing", ours: 5 },
];

// placeholder live values (swap for gateway data later)
const DEMO = { used: 6200, limit: FREE.requests, period: "this month" };

// pick the cheapest paid tier that covers a monthly request estimate
function recommendTier(perMonth) {
  if (perMonth <= FREE.requests) return FREE;
  const paid = TIERS.filter((t) => t.paid);
  return paid.find((t) => t.requests >= perMonth) || ENTERPRISE;
}

function UsageRing({ pct }) {
  const color = pct >= 90 ? "#f87171" : pct >= 75 ? "#fbbf24" : VIOLET;
  return (
    <div
      className="relative flex flex-col items-center justify-center shrink-0"
      style={{
        width: 118,
        height: 118,
        borderRadius: "50%",
        background: `conic-gradient(${color} 0% ${pct}%, rgba(255,255,255,0.08) ${pct}% 100%)`,
      }}
    >
      <div className="absolute rounded-full" style={{ inset: 9, background: "#222a3d" }} />
      <span className="relative text-[26px] font-bold" style={{ color }}>{pct}%</span>
    </div>
  );
}

function BalanceHero({ onSeePlans }) {
  const pct = Math.round((DEMO.used / DEMO.limit) * 100);
  return (
    <div className="glass-card dark:glass-card-dark rounded-2xl p-6 sm:p-7 flex flex-col md:flex-row md:items-center gap-6 mb-4 relative">
      <UsageRing pct={pct} />

      <div>
        <div className="text-[30px] font-bold text-white leading-tight">
          {fmt(DEMO.used)}{" "}
          <span className="text-[14px] font-normal text-white/50">
            / {fmt(DEMO.limit)} requests
          </span>
        </div>
        <div className="text-white/60 text-[13px] mt-1 mb-3">
          One pooled balance across geocode, search, directions, isochrone and every API.
        </div>
        <div className="flex flex-wrap gap-2">
          <span className="text-[11px] px-2.5 py-1 rounded-full bg-[rgba(52,211,153,0.13)] border border-[rgba(52,211,153,0.35)] text-emerald-300">
            Free plan
          </span>
          <span className="text-[11px] px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-white/70">
            {fmt(FREE.requests)} requests / month
          </span>
          <span className="text-[11px] px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-white/70">
            no credit card
          </span>
        </div>
      </div>

      <div className="flex-1" />

      <div className="text-right">
        <div className="text-[11px] text-white/45">Paid plans from</div>
        <div className="text-[20px] font-semibold text-white">
          €12<span className="text-[12px] text-white/50">/mo</span>
        </div>
        <button onClick={onSeePlans}
          className="mt-2 rounded-xl px-4 py-2.5 text-[13px] font-medium text-white"
          style={{ background: "linear-gradient(135deg,#8b5cf6,#6d28d9)" }}>
          See plans
        </button>
      </div>
    </div>
  );
}

// Per-client setup. Editable in one place so vendor menu changes are a one-line edit.
const CLIENTS = [
  { id: "claude", label: "Claude", steps: "Open Customize → Connectors → Add custom connector → paste your link → Save." },
  { id: "chatgpt", label: "ChatGPT", steps: "Settings → Connectors → Add → paste the link." },
  { id: "gemini", label: "Gemini", steps: "Add via Gemini settings, or CLI: gemini mcp add <link>." },
  { id: "cursor", label: "Cursor", steps: 'Easiest: paste the link in chat and say "use this MCP server". Or Settings → MCP → Add server.' },
  { id: "copilot", label: "Copilot", steps: 'Command Palette → "MCP: Add Server" → paste the link, or paste it in chat.' },
];

function ConnectCard() {
  const url = "https://mcp.mapatlas.org/u/8f2a4c91d3e7";
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [client, setClient] = useState("claude");
  const active = CLIENTS.find((c) => c.id === client);
  const masked = "https://mcp.mapatlas.org/u/••••••••••••";

  const copy = () => {
    navigator.clipboard?.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="glass-card dark:glass-card-dark rounded-2xl p-6">
      <div className="text-[11px] uppercase tracking-wider text-white/45 mb-2.5 font-medium">
        Connect your AI
      </div>
      <div className="text-[15px] text-white font-medium mb-3.5">
        Copy this link, paste it into your AI. That's it.
      </div>

      {/* Works with: interactive tabs */}
      <div className="text-[11px] uppercase tracking-wider text-white/40 mb-2.5">
        Works with · <span className="normal-case tracking-normal text-violet-300/80">click for setup</span>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-3.5">
        {CLIENTS.map((c) => (
          <button
            key={c.id}
            onClick={() => setClient(c.id)}
            className={`inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-[10px] border transition-colors ${
              client === c.id
                ? "bg-[rgba(167,139,250,0.22)] border-[rgba(167,139,250,0.55)] text-violet-200 font-semibold"
                : "bg-white/5 border-white/10 text-white/70 hover:bg-white/10"
            }`}
          >
            <span className="w-[7px] h-[7px] rounded-full"
              style={{ background: client === c.id ? VIOLET : "rgba(255,255,255,0.3)" }} />
            {c.label}
          </button>
        ))}
        <span className="text-[12px] text-white/45">+ many more</span>
      </div>

      {/* personal URL */}
      <div className="flex items-center gap-2 rounded-[10px] px-3 py-2.5 mb-2 text-[12px] font-mono"
        style={{ background: "rgba(0,0,0,0.28)", border: "1px solid rgba(255,255,255,0.1)" }}>
        <span className="truncate" style={{ color: revealed ? VIOLET : "rgba(255,255,255,0.7)" }}>
          {revealed ? url : masked}
        </span>
        <span className="flex-1" />
        <button onClick={() => setRevealed((r) => !r)}
          className="rounded-lg px-3 py-1 text-[11px] bg-[rgba(167,139,250,0.2)] border border-[rgba(167,139,250,0.4)] text-violet-200">
          {revealed ? "Hide" : "Reveal"}
        </button>
        <button onClick={copy}
          className="rounded-lg px-4 py-1 text-[11px] text-white"
          style={{ background: "linear-gradient(135deg,#8b5cf6,#6d28d9)" }}>
          {copied ? "Copied" : "Copy link"}
        </button>
      </div>
      <div className="flex items-center gap-2 mb-4">
        <button className="rounded-lg px-3 py-1 text-[11px] bg-white/5 border border-white/10 text-white/70">
          ↻ New link
        </button>
        <span className="text-[11px] text-white/40">
          Keep it private. Anyone with the link can use your requests.
        </span>
      </div>

      {/* per-client setup panel */}
      <div className="rounded-xl p-4"
        style={{ background: "rgba(167,139,250,0.08)", border: "1px solid rgba(167,139,250,0.25)" }}>
        <div className="text-[12px] font-semibold text-white mb-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: VIOLET }} />
          Set up in {active.label}
        </div>
        <div className="text-[12.5px] text-white/80 leading-relaxed">{active.steps}</div>
      </div>
    </div>
  );
}

// requests used this month, by API (placeholder; sums to DEMO.used)
const USAGE_BY_API = [
  { label: "Geocode / reverse", used: 2600 },
  { label: "Search / Nearby", used: 1500 },
  { label: "Directions", used: 1100 },
  { label: "Autocomplete", used: 600 },
  { label: "Isochrone", used: 250 },
  { label: "Other", used: 150 },
];

function UsageBreakdown() {
  const total = USAGE_BY_API.reduce((s, r) => s + r.used, 0);
  return (
    <div>
      <div className="flex items-baseline justify-between mb-3">
        <div className="text-[11px] uppercase tracking-wider text-white/45 font-medium">
          Requests this month
        </div>
        <div className="text-[12px] text-white/55">
          {fmt(total)} / {fmt(FREE.requests)}
        </div>
      </div>
      {USAGE_BY_API.map((r) => (
        <div key={r.label} className="mb-2.5">
          <div className="flex justify-between text-[12px] mb-1">
            <span className="text-white/75">{r.label}</span>
            <span className="text-white/55 font-mono">
              {fmt(r.used)}{" "}
              <span className="text-white/35">({Math.round((r.used / total) * 100)}%)</span>
            </span>
          </div>
          <div className="h-[7px] rounded-full bg-white/10 overflow-hidden">
            <div className="h-full rounded-full"
              style={{
                width: `${(r.used / total) * 100}%`,
                background: "linear-gradient(90deg,#8b5cf6,#a78bfa)",
              }} />
          </div>
        </div>
      ))}
      <div className="text-[11px] text-white/40 mt-1">
        Most calls = 1 request. A place / nearby search ≈ 5, advanced routing ≈ 2.
      </div>
    </div>
  );
}

// calculator inputs (weighted: search counts as 5 requests)
const CALC_ROWS = [
  { key: "geocode", label: "Geocodes / day", weight: WEIGHTS.geocode, max: 20000, init: 1200 },
  { key: "search", label: "Searches / day", weight: WEIGHTS.search, max: 5000, init: 300 },
  { key: "directions", label: "Directions / day", weight: WEIGHTS.directions, max: 20000, init: 900 },
];

function PlanCalculator({ onUpgrade }) {
  const [mix, setMix] = useState(
    CALC_ROWS.reduce((a, r) => ({ ...a, [r.key]: r.init }), {})
  );

  const perDay = useMemo(
    () => CALC_ROWS.reduce((sum, r) => sum + mix[r.key] * r.weight, 0),
    [mix]
  );
  const perMonth = perDay * 30;
  const tier = recommendTier(perMonth);
  const isFree = tier.name === "Free";

  return (
    <div className="mt-6 pt-5 border-t border-white/[0.07]">
      <div className="text-[11px] uppercase tracking-wider text-white/45 mb-3 font-medium">
        Which plan fits you?
      </div>
      {CALC_ROWS.map((r) => (
        <div key={r.key} className="mb-2.5">
          <div className="flex justify-between text-[12px] text-white/75 mb-1">
            <span>{r.label}{r.weight > 1 && <span className="text-white/35"> (×{r.weight})</span>}</span>
            <span className="text-violet-300 font-mono">{fmt(mix[r.key])}</span>
          </div>
          <input
            type="range"
            min={0}
            max={r.max}
            value={mix[r.key]}
            onChange={(e) => setMix((m) => ({ ...m, [r.key]: Number(e.target.value) }))}
            className="w-full"
            style={{ accentColor: VIOLET }}
          />
        </div>
      ))}
      <div className="rounded-xl px-4 py-3 flex justify-between items-center mt-2"
        style={{ background: "rgba(167,139,250,0.12)" }}>
        <div>
          <div className="text-[11px] text-white/55">
            ≈ {fmt(perDay)} requests/day · {short(perMonth)}/mo
          </div>
          <div className="text-[15px] font-semibold text-white">
            Recommended: <span className="text-violet-300">{tier.name}{tier.price ? ` ${tier.price}` : ""}</span>
          </div>
        </div>
        <button onClick={() => onUpgrade(isFree ? null : tier.name)}
          className="rounded-xl px-4 py-2.5 text-[13px] font-medium text-white"
          style={{ background: "linear-gradient(135deg,#8b5cf6,#6d28d9)" }}>
          {isFree ? "Start free" : "Upgrade"}
        </button>
      </div>
    </div>
  );
}

function PaygSection({ onChoose }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-5 pt-4 border-t border-white/[0.07]">
      <button onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between text-left">
        <div>
          <div className="text-[13px] font-semibold text-white">Pay as you go</div>
          <div className="text-[12px] text-white/55 mt-0.5">
            No monthly commitment. Pay per call, only for what you use.
          </div>
        </div>
        <svg className={`w-4 h-4 text-white/50 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <>
          <table className="w-full text-[12px] mt-3">
            <thead>
              <tr className="text-white/45 text-[10.5px] uppercase">
                <th className="text-left font-medium py-1.5">Call type</th>
                <th className="text-right font-medium py-1.5">Price / 1k requests</th>
              </tr>
            </thead>
            <tbody>
              {PAYG_RATES.map((r) => (
                <tr key={r.type} className="border-t border-white/[0.07]">
                  <td className="py-1.5 text-white/80">{r.type}</td>
                  <td className="py-1.5 text-right font-semibold text-violet-300">€{r.ours.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <button onClick={onChoose}
            className="mt-3 w-full rounded-lg px-3 py-2.5 text-[12.5px] font-medium text-white"
            style={{ background: "linear-gradient(135deg,#8b5cf6,#6d28d9)" }}>
            Choose
          </button>
        </>
      )}
    </div>
  );
}

function PlansModal({ open, onClose, highlight }) {
  const navigate = useNavigate();
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 overflow-y-auto"
      style={{ background: "rgba(8,11,20,0.6)", backdropFilter: "blur(4px)" }}
      onClick={onClose}>
      <div
        className="glass-card dark:glass-card-dark rounded-2xl p-6 w-full max-w-2xl relative my-8"
        style={{ background: "rgba(30,37,56,0.97)" }}
        onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose}
          className="absolute top-4 right-4 text-white/50 hover:text-white text-lg leading-none">×</button>
        <div className="text-[20px] font-semibold text-white mb-5">Choose a plan</div>

        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-white/45 text-[11px] uppercase">
              <th className="text-left font-medium py-2">Plan</th>
              <th className="text-right font-medium py-2">Requests / mo</th>
              <th className="text-right font-medium py-2">Price</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {TIERS.map((t) => {
              const on = !!t.recommended;
              return (
                <tr key={t.name} className="border-t border-white/[0.07]"
                  style={on ? { background: "rgba(167,139,250,0.12)" } : undefined}>
                  <td className="py-2.5 pl-2 text-white/90 font-medium">
                    {t.name}
                    {on && <span className="ml-2 text-[10px] uppercase tracking-wide text-violet-300">Recommended</span>}
                    <div className="text-[11px] text-white/45 font-normal">{t.blurb}</div>
                  </td>
                  <td className="py-2.5 text-right text-white/80">{fmt(t.requests)}</td>
                  <td className="py-2.5 text-right font-semibold text-white whitespace-nowrap">
                    {t.price}{t.paid && <span className="text-white/45 font-normal">/mo</span>}
                  </td>
                  <td className="py-2.5 pl-3 pr-1 text-right">
                    <button onClick={() => navigate(t.paid ? "/billing" : "/mcp")}
                      className="rounded-lg px-3 py-1.5 text-[11px] font-medium text-white"
                      style={{ background: t.paid
                        ? "linear-gradient(135deg,#8b5cf6,#6d28d9)"
                        : "rgba(255,255,255,0.08)" }}>
                      {t.paid ? "Choose" : "Current"}
                    </button>
                  </td>
                </tr>
              );
            })}
            <tr className="border-t border-white/[0.07]">
              <td className="py-2.5 pl-2 text-white/90 font-medium">
                {ENTERPRISE.name}
                <div className="text-[11px] text-white/45 font-normal">{ENTERPRISE.blurb}</div>
              </td>
              <td className="py-2.5 text-right text-white/80">Custom</td>
              <td className="py-2.5 text-right font-semibold text-white whitespace-nowrap">{ENTERPRISE.price}</td>
              <td className="py-2.5 pl-3 pr-1 text-right">
                <button onClick={() => navigate("/billing")}
                  className="rounded-lg px-3 py-1.5 text-[11px] font-medium text-violet-200 bg-white/5 border border-white/10 whitespace-nowrap">
                  Talk to us
                </button>
              </td>
            </tr>
          </tbody>
        </table>

        <PaygSection onChoose={() => navigate("/billing")} />
      </div>
    </div>,
    document.body
  );
}

export function MCP() {
  const [showPlans, setShowPlans] = useState(false);
  const [highlight, setHighlight] = useState(null);
  const openPlans = (tierName) => { setHighlight(tierName || null); setShowPlans(true); };

  useEffect(() => {
    document.body.style.overflowX = "hidden";
    document.documentElement.style.overflowX = "hidden";
    return () => {
      document.body.style.overflowX = "auto";
      document.documentElement.style.overflowX = "auto";
    };
  }, []);

  return (
    <Fragment>
      <div className="min-h-screen w-full overflow-x-hidden">
        <div className="py-3 w-full box-border px-6 overflow-x-hidden">
          <BalanceHero onSeePlans={() => openPlans()} />

          <div className="flex flex-col xl:flex-row gap-4">
            <div className="flex-[1.15] min-w-0">
              <ConnectCard />
            </div>
            <div className="flex-1 min-w-0">
              <div className="glass-card dark:glass-card-dark rounded-2xl p-6">
                <UsageBreakdown />
                <PlanCalculator onUpgrade={openPlans} />
              </div>
            </div>
          </div>

          <div className="text-[11px] text-white/40 mt-4">
            By connecting and using the API, you agree to the{" "}
            <Link to="/terms" className="text-violet-300 underline">Terms of Use</Link>.
            The underlying map and place data remains owned by us.
          </div>
        </div>
      </div>
      <PlansModal open={showPlans} onClose={() => setShowPlans(false)} highlight={highlight} />
    </Fragment>
  );
}
